import React, { useState, useEffect, useMemo } from "react";
import { 
  ArrowUpRight, 
  ArrowDownRight, 
  RotateCcw, 
  Lock, 
  ArrowRight,
  Settings,
  PlusCircle,
  TrendingUp,
  History,
  Activity,
  Layers,
  X,
  Sliders,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { 
  loadPaperAccount, 
  resetPaperAccount, 
  depositPaperFunds, 
  openPaperPosition, 
  closePaperPosition, 
  checkAutoSlTp, 
  calculatePositionPnL, 
  getPaperStatistics
} from "../services/paperTradingService";

const QUICK_LOTS = ["0.01", "0.05", "0.10", "0.50", "1.00"];

export default function PaperTradingPanel({ currentSymbol, tick, onWorkspaceChange }) {
  const { user, canAccess, openAuth } = useAuth();
  const userId = user?.id || "guest";
  const isAccessible = canAccess('PAPER_TRADING');

  // Account State from persistent service
  const [account, setAccount] = useState(() => loadPaperAccount(userId));
  const [activeTab, setActiveTab] = useState("trade"); // "trade" | "positions" | "history" | "stats"
  const [lotSize, setLotSize] = useState("0.10");
  const [slPrice, setSlPrice] = useState("");
  const [tpPrice, setTpPrice] = useState("");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [customResetAmount, setCustomResetAmount] = useState(10000);
  const [depositAmount, setDepositAmount] = useState(1000);
  const [actionNotice, setActionNotice] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem("minopro_paper_trading_collapsed") === "true";
    } catch (e) {
      return false;
    }
  });

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem("minopro_paper_trading_collapsed", String(next)); } catch (e) {}
      return next;
    });
  };

  // Reload account when user changes (e.g. login/logout) or when updated from Profile modal
  useEffect(() => {
    const acc = loadPaperAccount(userId);
    setAccount(acc);
    setCustomResetAmount(acc.initialBalance || 10000);

    const handleUpdate = (e) => {
      if (e.detail?.userId === userId && e.detail?.account) {
        setAccount(e.detail.account);
        setCustomResetAmount(e.detail.account.initialBalance || 10000);
      }
    };
    window.addEventListener("minopro_paper_account_updated", handleUpdate);
    return () => window.removeEventListener("minopro_paper_account_updated", handleUpdate);
  }, [userId]);

  // Sync to Cloud Workspace whenever account updates
  const syncToCloud = (updatedAcc) => {
    if (onWorkspaceChange) {
      onWorkspaceChange({ paperTrading: updatedAcc });
    }
  };

  const currentPrice = tick?.last || tick?.close || tick?.bid || 0;
  const askPrice = tick?.ask || currentPrice;
  const bidPrice = tick?.bid || currentPrice;

  const formatPrice = (p) => {
    if (!p || p <= 0) return "--";
    if (p < 5.0) return Number(p).toFixed(5);
    if (p < 500.0) return Number(p).toFixed(3);
    return Number(p).toFixed(2);
  };

  // Real-time Auto SL / TP checking
  useEffect(() => {
    if (!currentPrice || account.positions.length === 0) return;
    const triggeredAcc = checkAutoSlTp(userId, currentSymbol, currentPrice);
    if (triggeredAcc) {
      setAccount(triggeredAcc);
      syncToCloud(triggeredAcc);
      setActionNotice({ type: "info", text: "Đã tự động đóng lệnh theo SL/TP chạm giá!" });
      setTimeout(() => setActionNotice(null), 3500);
    }
  }, [currentPrice, currentSymbol, userId, account.positions]);

  // Calculate live floating PnL across all open positions
  const { floatingPnL, equity } = useMemo(() => {
    let floatSum = 0;
    account.positions.forEach((pos) => {
      const priceToUse = pos.symbol === currentSymbol ? currentPrice : pos.entryPrice;
      const { pnl } = calculatePositionPnL(pos, priceToUse);
      floatSum += pnl;
    });
    const roundFloat = Math.round(floatSum * 100) / 100;
    const eq = Math.round((account.balance + roundFloat) * 100) / 100;
    return { floatingPnL: roundFloat, equity: eq };
  }, [account.positions, account.balance, currentPrice, currentSymbol]);

  // Open Position Handler
  const handleOpenPosition = (type) => {
    const execPrice = type === "BUY" ? askPrice : bidPrice;
    if (!execPrice || execPrice <= 0) {
      setActionNotice({ type: "error", text: "Chưa có giá thời gian thực để vào lệnh!" });
      setTimeout(() => setActionNotice(null), 2500);
      return;
    }

    const numLot = parseFloat(lotSize) || 0.1;
    const numSl = slPrice ? parseFloat(slPrice) : null;
    const numTp = tpPrice ? parseFloat(tpPrice) : null;

    const updated = openPaperPosition(userId, {
      symbol: currentSymbol,
      type,
      lotSize: numLot,
      entryPrice: execPrice,
      sl: numSl,
      tp: numTp,
    });

    setAccount(updated);
    syncToCloud(updated);
    setSlPrice("");
    setTpPrice("");
    setActionNotice({ type: "success", text: `Đã mở lệnh ${type} ${numLot}L ${currentSymbol} @ ${formatPrice(execPrice)}` });
    setTimeout(() => setActionNotice(null), 3000);
  };

  // Close Position Handler
  const handleClose = (posId) => {
    const pos = account.positions.find((p) => p.id === posId);
    if (!pos) return;

    const priceToClose = pos.symbol === currentSymbol ? currentPrice : pos.entryPrice;
    const updated = closePaperPosition(userId, posId, priceToClose, "MANUAL");
    setAccount(updated);
    syncToCloud(updated);
    setActionNotice({ type: "info", text: `Đã đóng lệnh ${pos.type} ${pos.symbol} @ ${formatPrice(priceToClose)}` });
    setTimeout(() => setActionNotice(null), 2500);
  };

  // Close All Positions
  const handleCloseAll = () => {
    if (account.positions.length === 0) return;
    let acc = account;
    account.positions.forEach((pos) => {
      const priceToClose = pos.symbol === currentSymbol ? currentPrice : pos.entryPrice;
      acc = closePaperPosition(userId, pos.id, priceToClose, "MANUAL");
    });
    setAccount(acc);
    syncToCloud(acc);
    setActionNotice({ type: "info", text: "Đã đóng toàn bộ lệnh đang mở!" });
    setTimeout(() => setActionNotice(null), 2500);
  };

  // Reset Account Capital
  const handleResetCapital = (clearHist = false) => {
    const amt = parseFloat(customResetAmount) || 10000;
    const updated = resetPaperAccount(userId, amt, clearHist);
    setAccount(updated);
    syncToCloud(updated);
    setIsSettingsOpen(false);
    setActionNotice({ type: "success", text: `Đã đặt lại số vốn về $${amt.toLocaleString()} USD!` });
    setTimeout(() => setActionNotice(null), 3500);
  };

  // Deposit More Funds
  const handleDepositFunds = () => {
    const amt = parseFloat(depositAmount) || 1000;
    const updated = depositPaperFunds(userId, amt);
    setAccount(updated);
    syncToCloud(updated);
    setIsSettingsOpen(false);
    setActionNotice({ type: "success", text: `Đã nạp thêm +$${amt.toLocaleString()} USD!` });
    setTimeout(() => setActionNotice(null), 3500);
  };

  const stats = useMemo(() => getPaperStatistics(account.tradeHistory), [account.tradeHistory]);

  return (
    <div className="bg-slate-900/90 border border-slate-800/80 rounded-xl p-3 flex flex-col gap-2.5 shadow-xl relative overflow-hidden backdrop-blur-md font-sans">
      
      {/* 1. Slim Integrated Header & Financial Badges */}
      <div className={`flex flex-wrap items-center justify-between gap-2 ${isCollapsed ? "" : "border-b border-slate-800/80 pb-2"}`}>
        {/* Left: Brand & Status */}
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse inline-block" />
          <h3 className="font-extrabold text-xs text-slate-100 uppercase tracking-tight flex items-center gap-1.5">
            <span>Paper Trading</span>
            <span className="text-[9px] px-1.5 py-0.2 rounded bg-slate-800 text-cyan-300 font-mono border border-slate-700 font-semibold">
              Live Feed
            </span>
          </h3>
        </div>

        {/* Center: Minimalist Segmented Tabs */}
        {!isCollapsed && isAccessible && (
          <div className="flex items-center gap-0.5 bg-slate-950 p-0.5 rounded-lg border border-slate-800 text-xs overflow-x-auto scrollbar-none max-w-full">
            <button
              onClick={() => setActiveTab("trade")}
              className={`px-2 sm:px-2.5 py-1 rounded font-bold transition cursor-pointer flex items-center gap-1 shrink-0 ${
                activeTab === "trade"
                  ? "bg-slate-800 text-cyan-300 shadow-sm"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              <Activity className="w-3 h-3" />
              <span>Đặt Lệnh</span>
            </button>

            <button
              onClick={() => setActiveTab("positions")}
              className={`px-2 sm:px-2.5 py-1 rounded font-bold transition cursor-pointer flex items-center gap-1 shrink-0 ${
                activeTab === "positions"
                  ? "bg-slate-800 text-cyan-300 shadow-sm"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              <Layers className="w-3 h-3" />
              <span>Lệnh Mở</span>
              {account.positions.length > 0 && (
                <span className="px-1 py-0.2 rounded-full bg-cyan-500 text-slate-950 text-[9.5px] font-black leading-none">
                  {account.positions.length}
                </span>
              )}
            </button>

            <button
              onClick={() => setActiveTab("history")}
              className={`px-2 sm:px-2.5 py-1 rounded font-bold transition cursor-pointer flex items-center gap-1 shrink-0 ${
                activeTab === "history"
                  ? "bg-slate-800 text-cyan-300 shadow-sm"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              <History className="w-3 h-3" />
              <span>Lịch Sử ({account.tradeHistory.length})</span>
            </button>

            <button
              onClick={() => setActiveTab("stats")}
              className={`px-2 sm:px-2.5 py-1 rounded font-bold transition cursor-pointer flex items-center gap-1 shrink-0 ${
                activeTab === "stats"
                  ? "bg-slate-800 text-cyan-300 shadow-sm"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              <TrendingUp className="w-3 h-3" />
              <span>Hiệu Suất</span>
            </button>
          </div>
        )}

        {/* Right: Sleek Financial Metrics */}
        <div className="flex items-center gap-1.5 font-mono text-[11px] sm:text-xs flex-wrap">
          <div className="px-2 py-0.5 rounded-lg bg-slate-950 border border-slate-800 flex items-center gap-1">
            <span className="text-slate-400 text-[9.5px] sm:text-[10px] font-sans">Số Dư:</span>
            <span className="font-bold text-slate-100">
              ${account.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          <div className="px-2 py-0.5 rounded-lg bg-slate-950 border border-slate-800 flex items-center gap-1">
            <span className="text-slate-400 text-[9.5px] sm:text-[10px] font-sans">Vốn Thực:</span>
            <span className={`font-bold ${equity >= account.balance ? "text-emerald-400" : "text-rose-400"}`}>
              ${equity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          <div className={`px-2 py-0.5 rounded-lg border flex items-center gap-1 ${
            floatingPnL > 0 
              ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" 
              : floatingPnL < 0 
              ? "bg-rose-500/10 border-rose-500/30 text-rose-400" 
              : "bg-slate-950 border-slate-800 text-slate-400"
          }`}>
            <span className="text-[9.5px] sm:text-[10px] font-sans">PnL:</span>
            <span className="font-bold">
              {floatingPnL > 0 ? "+" : ""}${floatingPnL.toFixed(2)}
            </span>
          </div>

          {!isCollapsed && isAccessible && (
            <button
              onClick={() => setIsSettingsOpen(true)}
              className="p-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-cyan-300 transition cursor-pointer border border-slate-700"
              title="Cài đặt vốn & Đặt lại tài khoản"
            >
              <Settings className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Collapse / Expand Toggle */}
          <button
            onClick={toggleCollapse}
            className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-cyan-300 border border-slate-700 transition cursor-pointer flex items-center gap-1 text-xs font-semibold shadow-sm"
            title={isCollapsed ? "Mở rộng Paper Trading" : "Thu gọn Paper Trading"}
          >
            <span className="text-[11px] hidden sm:inline">{isCollapsed ? "Mở rộng" : "Thu gọn"}</span>
            {isCollapsed ? <ChevronDown className="w-3.5 h-3.5 text-cyan-400" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <>

      {/* Action Notification Toast */}
      {actionNotice && (
        <div className={`px-2.5 py-1 rounded-lg text-xs font-medium flex items-center justify-between animate-fadeIn ${
          actionNotice.type === "success"
            ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
            : actionNotice.type === "error"
            ? "bg-rose-500/15 text-rose-300 border border-rose-500/30"
            : "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"
        }`}>
          <span>{actionNotice.text}</span>
          <button onClick={() => setActionNotice(null)} className="text-slate-400 hover:text-slate-200 ml-2">✕</button>
        </div>
      )}

      {/* Lock Gate for Unauthenticated Users */}
      {!isAccessible ? (
        <div className="py-5 px-4 bg-slate-950/60 border border-slate-800 rounded-xl text-center space-y-2">
          <div className="w-8 h-8 rounded-xl bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center justify-center mx-auto">
            <Lock className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-200">Giao dịch Mô phỏng (Paper Trading)</h4>
            <p className="text-[11px] text-slate-400">
              Đăng nhập tài khoản Basic (Miễn phí) để đặt lệnh thử nghiệm và lưu lịch sử giao dịch.
            </p>
          </div>
          <button
            onClick={() => openAuth('register')}
            className="px-3.5 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-bold transition inline-flex items-center gap-1.5 cursor-pointer shadow-md"
          >
            <span>Đăng Ký Miễn Phí</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <>
          {/* TAB 1: QUICK TRADE EXECUTION (Ultra Clean & Compact) */}
          {activeTab === "trade" && (
            <div className="space-y-2">
              {/* Parameters Input Row */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center bg-slate-950/60 p-2 rounded-xl border border-slate-800/80">
                {/* Lot Size + Quick Pills */}
                <div className="sm:col-span-4 flex items-center gap-1.5">
                  <div className="relative flex-1">
                    <span className="absolute left-2 top-1.5 text-[10px] font-bold text-slate-500 pointer-events-none uppercase">Lot</span>
                    <input
                      type="number"
                      min="0.01"
                      max="100.0"
                      step="0.01"
                      value={lotSize}
                      onChange={(e) => setLotSize(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700/80 text-cyan-300 text-xs font-bold rounded-lg pl-9 pr-2 py-1 font-mono focus:outline-none focus:border-cyan-500 text-right"
                    />
                  </div>

                  {/* Quick Lot Pills */}
                  <div className="flex items-center gap-0.5">
                    {QUICK_LOTS.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => setLotSize(q)}
                        className={`px-1.5 py-1 rounded text-[10px] font-mono font-bold transition cursor-pointer border ${
                          lotSize === q
                            ? "bg-cyan-500/25 text-cyan-300 border-cyan-500/50"
                            : "bg-slate-900 text-slate-400 hover:text-slate-200 border-slate-800"
                        }`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Optional SL Price */}
                <div className="sm:col-span-3">
                  <div className="relative">
                    <span className="absolute left-2 top-1.5 text-[10px] font-bold text-rose-500/80 pointer-events-none uppercase">SL</span>
                    <input
                      type="number"
                      step="any"
                      placeholder="Cắt lỗ..."
                      value={slPrice}
                      onChange={(e) => setSlPrice(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700/80 text-rose-300 placeholder-slate-600 text-xs font-bold rounded-lg pl-7 pr-2 py-1 font-mono focus:outline-none focus:border-rose-500 text-right"
                    />
                  </div>
                </div>

                {/* Optional TP Price */}
                <div className="sm:col-span-3">
                  <div className="relative">
                    <span className="absolute left-2 top-1.5 text-[10px] font-bold text-emerald-500/80 pointer-events-none uppercase">TP</span>
                    <input
                      type="number"
                      step="any"
                      placeholder="Chốt lời..."
                      value={tpPrice}
                      onChange={(e) => setTpPrice(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700/80 text-emerald-300 placeholder-slate-600 text-xs font-bold rounded-lg pl-7 pr-2 py-1 font-mono focus:outline-none focus:border-emerald-500 text-right"
                    />
                  </div>
                </div>

                {/* Symbol Tag & Current Price */}
                <div className="sm:col-span-2 text-right font-mono flex sm:flex-col justify-between items-center sm:items-end">
                  <span className="font-extrabold text-xs text-amber-300">{currentSymbol}</span>
                  <span className="text-[11px] text-slate-400 font-bold">@{formatPrice(currentPrice)}</span>
                </div>
              </div>

              {/* BUY / SELL Action Buttons (Ergonomic & Professional) */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handleOpenPosition("BUY")}
                  className="py-2.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-slate-950 font-black text-xs sm:text-sm transition shadow-md shadow-emerald-500/10 cursor-pointer active:scale-[0.98] flex items-center justify-between"
                >
                  <div className="flex items-center gap-1.5">
                    <ArrowUpRight className="w-4 h-4 stroke-[3]" />
                    <span>MUA (BUY)</span>
                  </div>
                  <span className="font-mono text-xs font-bold opacity-90">@{formatPrice(askPrice)}</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleOpenPosition("SELL")}
                  className="py-2.5 px-4 rounded-xl bg-gradient-to-r from-rose-600 via-rose-500 to-red-500 hover:from-rose-500 hover:to-red-400 text-slate-950 font-black text-xs sm:text-sm transition shadow-md shadow-rose-500/10 cursor-pointer active:scale-[0.98] flex items-center justify-between"
                >
                  <div className="flex items-center gap-1.5">
                    <ArrowDownRight className="w-4 h-4 stroke-[3]" />
                    <span>BÁN (SELL)</span>
                  </div>
                  <span className="font-mono text-xs font-bold opacity-90">@{formatPrice(bidPrice)}</span>
                </button>
              </div>
            </div>
          )}

          {/* TAB 2: ACTIVE POSITIONS */}
          {activeTab === "positions" && (
            <div className="space-y-1.5">
              {account.positions.length === 0 ? (
                <div className="py-6 text-center bg-slate-950/40 rounded-xl border border-slate-800/80 text-slate-500 text-xs">
                  Hiện chưa có lệnh mô phỏng nào đang mở.
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between px-1 text-[11px] text-slate-400">
                    <span>Danh sách ({account.positions.length} vị thế đang mở)</span>
                    {account.positions.length > 1 && (
                      <button
                        onClick={handleCloseAll}
                        className="px-2 py-0.5 text-[10.5px] font-bold rounded bg-rose-500/15 hover:bg-rose-500/25 text-rose-400 border border-rose-500/30 transition cursor-pointer"
                      >
                        Đóng Toàn Bộ
                      </button>
                    )}
                  </div>
                  <div className="space-y-1 max-h-48 overflow-y-auto pr-0.5">
                    {account.positions.map((pos) => {
                      const priceToUse = pos.symbol === currentSymbol ? currentPrice : pos.entryPrice;
                      const { pnl, pnlPips } = calculatePositionPnL(pos, priceToUse);
                      const isProfit = pnl >= 0;

                      return (
                        <div
                          key={pos.id}
                          className="flex items-center justify-between p-2 rounded-lg bg-slate-950/70 border border-slate-800 text-xs font-mono hover:border-slate-700 transition"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-1.5 py-0.2 rounded font-sans font-black text-[10px] border ${
                                pos.type === "BUY"
                                  ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                                  : "bg-rose-500/15 text-rose-400 border-rose-500/30"
                              }`}
                            >
                              {pos.type} {pos.lotSize}L
                            </span>
                            <span className="font-bold text-slate-200">{pos.symbol}</span>
                            <span className="text-slate-400 text-[11px]">@{formatPrice(pos.entryPrice)}</span>
                            {pos.sl && <span className="text-[10px] text-rose-400/80 font-sans">SL:{formatPrice(pos.sl)}</span>}
                            {pos.tp && <span className="text-[10px] text-emerald-400/80 font-sans">TP:{formatPrice(pos.tp)}</span>}
                          </div>

                          <div className="flex items-center gap-2.5">
                            <div className="text-right">
                              <span className={`font-black text-xs ${isProfit ? "text-emerald-400" : "text-rose-400"}`}>
                                {isProfit ? "+" : ""}${pnl.toFixed(2)}
                              </span>
                              <span className="text-[9.5px] text-slate-500 ml-1">
                                ({isProfit ? "+" : ""}{pnlPips}p)
                              </span>
                            </div>
                            <button
                              onClick={() => handleClose(pos.id)}
                              className="px-2 py-0.5 rounded bg-slate-800 hover:bg-rose-500/20 text-slate-300 hover:text-rose-300 border border-slate-700 hover:border-rose-500/30 text-[11px] font-sans font-bold transition cursor-pointer"
                            >
                              Đóng
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* TAB 3: TRADE HISTORY */}
          {activeTab === "history" && (
            <div className="space-y-1.5">
              {account.tradeHistory.length === 0 ? (
                <div className="py-6 text-center bg-slate-950/40 rounded-xl border border-slate-800/80 text-slate-500 text-xs">
                  Chưa có lịch sử lệnh đã đóng.
                </div>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto pr-0.5">
                  {account.tradeHistory.map((t, idx) => {
                    const isWin = t.pnl > 0;
                    const isLoss = t.pnl < 0;

                    return (
                      <div
                        key={t.id || idx}
                        className="flex items-center justify-between p-1.5 px-2 rounded-lg bg-slate-950/60 border border-slate-800/80 text-xs font-mono"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-1 py-0.2 rounded font-sans font-bold text-[9.5px] ${
                              t.type === "BUY" ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"
                            }`}
                          >
                            {t.type} {t.lotSize}L
                          </span>
                          <span className="font-bold text-slate-300">{t.symbol}</span>
                          <span className="text-slate-500 text-[10.5px]">
                            {formatPrice(t.entryPrice)} → {formatPrice(t.closePrice)}
                          </span>
                          {t.closeReason && (
                            <span className="text-[9px] px-1 py-0.2 rounded bg-slate-800 text-slate-400 font-sans">
                              {t.closeReason === "TP" ? "🎯 TP" : t.closeReason === "SL" ? "🛡️ SL" : "Tay"}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-500 font-sans">{t.closeTimeDisplay || ""}</span>
                          <span
                            className={`font-bold text-xs px-1.5 py-0.2 rounded ${
                              isWin
                                ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                                : isLoss
                                ? "bg-rose-500/15 text-rose-400 border border-rose-500/30"
                                : "bg-slate-800 text-slate-300"
                            }`}
                          >
                            {isWin ? "+" : ""}${t.pnl.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 4: PERFORMANCE & STATISTICS */}
          {activeTab === "stats" && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="p-2 rounded-xl bg-slate-950/70 border border-slate-800 text-center">
                <span className="text-[10px] text-slate-400 block font-sans">Tỷ Lệ Thắng</span>
                <span className="font-extrabold text-xs text-cyan-400 font-mono">{stats.winRate}%</span>
                <span className="text-[9.5px] text-slate-500 block font-sans">
                  {stats.wins}W / {stats.losses}L
                </span>
              </div>

              <div className="p-2 rounded-xl bg-slate-950/70 border border-slate-800 text-center">
                <span className="text-[10px] text-slate-400 block font-sans">Lợi Nhuận Ròng</span>
                <span className={`font-extrabold text-xs font-mono ${(stats?.netPnL ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {(stats?.netPnL ?? 0) >= 0 ? "+" : ""}${Number(stats?.netPnL ?? 0).toFixed(2)}
                </span>
                <span className="text-[9.5px] text-slate-500 block font-sans">{stats?.totalTrades ?? 0} Lệnh chốt</span>
              </div>

              <div className="p-2 rounded-xl bg-slate-950/70 border border-slate-800 text-center">
                <span className="text-[10px] text-slate-400 block font-sans">Profit Factor</span>
                <span className="font-extrabold text-xs text-slate-200 font-mono block">
                  {stats?.profitFactor ?? 1}
                </span>
                <span className="text-[9.5px] text-slate-500 block font-sans">+${stats?.grossProfit ?? 0}/-${stats?.grossLoss ?? 0}</span>
              </div>

              <div className="p-2 rounded-xl bg-slate-950/70 border border-slate-800 text-center">
                <span className="text-[10px] text-slate-400 block font-sans">Tăng Trưởng</span>
                <span className={`font-extrabold text-xs font-mono ${
                  equity >= (account?.initialBalance ?? 10000) ? "text-emerald-400" : "text-rose-400"
                }`}>
                  {(account?.initialBalance ?? 0) > 0
                    ? `${(((equity - account.initialBalance) / account.initialBalance) * 100).toFixed(2)}%`
                    : "0%"}
                </span>
                <span className="text-[9.5px] text-slate-500 block font-sans">
                  Khởi điểm: ${Number(account?.initialBalance ?? 10000).toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* MODAL: RESET CAPITAL & DEPOSIT VIRTUAL FUNDS */}
      {isSettingsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl flex flex-col font-sans">
            
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-slate-950/50">
              <div className="flex items-center gap-2">
                <Sliders className="w-4 h-4 text-cyan-400" />
                <h4 className="font-bold text-sm text-slate-100">Cài Đặt Vốn & Đặt Lại Tài Khoản</h4>
              </div>
              <button
                onClick={() => setIsSettingsOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4 text-xs text-slate-300">
              
              {/* Option 1: Reset Capital */}
              <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-100 flex items-center gap-1.5">
                    <RotateCcw className="w-3.5 h-3.5 text-amber-400" />
                    <span>Đặt Lại Số Vốn Ban Đầu (Reset)</span>
                  </span>
                </div>
                <p className="text-[11px] text-slate-400">
                  Nhập số dư ban đầu mong muốn để thử nghiệm lại từ đầu.
                </p>

                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="100"
                    step="500"
                    value={customResetAmount}
                    onChange={(e) => setCustomResetAmount(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs font-mono font-bold text-cyan-300 text-right focus:outline-none focus:border-cyan-500"
                    placeholder="Nhập số tiền USD..."
                  />
                  <span className="font-bold text-slate-400 font-mono">USD</span>
                </div>

                <div className="flex items-center gap-2 pt-0.5">
                  <button
                    onClick={() => handleResetCapital(false)}
                    className="flex-1 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 font-bold transition cursor-pointer text-center"
                  >
                    Đặt Lại Số Dư (Giữ Lịch Sử)
                  </button>
                  <button
                    onClick={() => handleResetCapital(true)}
                    className="py-1.5 px-3 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40 font-bold transition cursor-pointer text-center"
                    title="Xóa cả lịch sử lệnh"
                  >
                    Xóa Hết
                  </button>
                </div>
              </div>

              {/* Option 2: Deposit Virtual Funds */}
              <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-100 flex items-center gap-1.5">
                    <PlusCircle className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Nạp Thêm Vốn Ảo (Deposit)</span>
                  </span>
                </div>
                <p className="text-[11px] text-slate-400">
                  Cộng thêm tiền vào số dư hiện tại mà không làm thay đổi các lệnh đang mở.
                </p>

                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="100"
                    step="500"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-3 py-1.5 text-xs font-mono font-bold text-emerald-300 text-right focus:outline-none focus:border-emerald-500"
                  />
                  <span className="font-bold text-slate-400 font-mono">USD</span>
                </div>

                <button
                  onClick={handleDepositFunds}
                  className="w-full py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold transition cursor-pointer shadow-md shadow-emerald-500/20 text-center"
                >
                  Nạp Thêm Vốn Vào Số Dư
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
        </>
      )}

    </div>
  );
}